/**
 * Route-Level Page Caching Hook
 * Caches page data when navigating away, restores when returning
 * Makes navigation feel INSTANT - no reloading!
 * 
 * Example: Go to Campaigns → Ad Groups → Back to Campaigns
 * Result: Campaigns data loads instantly from cache!
 */

import { useState, useEffect, useRef } from 'react';
import { clearAllLiveEntityCache } from '../utils/liveEntityCache';

// Global page cache storage (survives component unmounts)
const pageCache = new Map();

// Cache configuration
const DEFAULT_TTL = 5 * 60 * 1000; // 5 minutes

/**
 * Clear cache entry
 */
export const clearPageCache = (key) => {
  pageCache.delete(key);
};

/**
 * Clear all page cache
 */
export const clearAllPageCache = () => {
  pageCache.clear();
  clearAllLiveEntityCache();
};

/**
 * Hook for page-level caching
 * 
 * Usage:
 * const { cachedData, setCachedData, isFromCache } = usePageCache('campaigns', profileId);
 * 
 * if (cachedData) {
 *   // Use cached data immediately
 *   setCampaigns(cachedData);
 * }
 * 
 * // Then fetch fresh data in background
 * fetchData().then(data => {
 *   setCampaigns(data);
 *   setCachedData(data); // Update cache
 * });
 */
/** Synchronous read for initial state (e.g. categorization when returning from hourly tab). */
export function readPageCache(pageKey, dependencies = [], ttl = DEFAULT_TTL) {
  const cacheKey = `${pageKey}_${JSON.stringify(dependencies)}`;
  const cached = pageCache.get(cacheKey);
  if (!cached) return null;
  if (Date.now() - cached.timestamp >= (cached.ttl ?? ttl)) {
    pageCache.delete(cacheKey);
    return null;
  }
  return cached.data;
}

export function usePageCache(pageKey, dependencies = []) {
  const [cachedData, setCachedDataState] = useState(null);
  const [isFromCache, setIsFromCache] = useState(false);
  const mountedRef = useRef(true);
  
  // Generate unique cache key including dependencies (e.g., profileId)
  const cacheKey = `${pageKey}_${JSON.stringify(dependencies)}`;
  
  // Load cached data on mount
  useEffect(() => {
    mountedRef.current = true;
    
    const cached = pageCache.get(cacheKey);
    if (cached) {
      const now = Date.now();
      const age = now - cached.timestamp;
      
      // Check if cache is still valid
      if (age < cached.ttl) {
        setCachedDataState(cached.data);
        setIsFromCache(true);
      } else {
        pageCache.delete(cacheKey);
        setIsFromCache(false);
      }
    } else {
      pageCache.delete(cacheKey);
      setCachedDataState(null);
      setIsFromCache(false);
    }
    
    return () => {
      mountedRef.current = false;
    };
  }, [cacheKey, pageKey]);
  
  /**
   * Update cached data
   */
  const setCachedData = (data, ttl = DEFAULT_TTL) => {
    if (!mountedRef.current) return;
    
    // Check if data is actually different to prevent unnecessary saves
    const cached = pageCache.get(cacheKey);
    if (cached && JSON.stringify(cached.data) === JSON.stringify(data)) {
      // Data hasn't changed, skip save
      return;
    }
    
    pageCache.set(cacheKey, {
      data,
      timestamp: Date.now(),
      ttl
    });
    
    setCachedDataState(data);
  };
  
  /**
   * Clear this page's cache
   */
  const clearCache = () => {
    pageCache.delete(cacheKey);
    setCachedDataState(null);
    setIsFromCache(false);
  };
  
  return {
    cachedData,
    setCachedData,
    isFromCache,
    clearCache
  };
}

/**
 * Hook to clear page cache when store changes
 * Use in Layout or top-level components
 */
export function useClearPageCacheOnStoreChange(selectedProfile) {
  const previousProfileRef = useRef(null);
  
  useEffect(() => {
    if (!selectedProfile) return;
    
    const currentProfileId = selectedProfile.profileId;
    const previousProfileId = previousProfileRef.current;
    
    // Detect store switch
    if (previousProfileId && previousProfileId !== currentProfileId) {
      clearAllPageCache();
    }
    
    previousProfileRef.current = currentProfileId;
  }, [selectedProfile]);
}

export default usePageCache;

